import { Button, Col, Row, Select, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import VerticalCenter from "src/components/WrapCenter/VerticalCenter";
import { useDidMount } from "src/hook/useDidMount";
import { profileStore } from "src/stores/profile-store";
import { reportFlareStore } from "src/stores/report-flare-volume-store";
import { useShallow } from "zustand/react/shallow";
import { BsCardList, BsTable } from "react-icons/bs";
import { componentPalette } from "src/theme/component-palette";
import { pdfStore } from "src/stores/pdf-store";
import { canApproveAndViewMainReport } from "../../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { formatNumberWithRoundUpDecimal } from "src/utils/process-number";
import { excelStore } from "src/stores/excel-store.interface";
import { MONTHS_LISTS } from "src/entity/common/time.constant";
import { capitalizeFirstLetter } from "src/utils/manage-string";

const { Title, Text } = Typography;

interface ReportGovernmentComponentProps {}

const ReportGovernmentComponent: React.FC<ReportGovernmentComponentProps> = (props) => {
  const [selectedYear, setSelectedYear] = useState<string | null>("");
  const [selectedMonth, setSelectedMonth] = useState<string | null>("jan");
  const [selectedFlareName, setSelectedFlareName] = useState<string | null>("");
  const [selectedButton, setSelectedButton] = useState("list");
  const { permissions } = useNavigationMenu();

  const profile = profileStore(useShallow((state) => state.profile));

  const loading = reportFlareStore(useShallow((state) => state.loadingReportGovernment));
  const loadingFilterReportGovernment = reportFlareStore(useShallow((state) => state.loadingFilterReportGovernment));
  const getAllFilterReportGovernment = reportFlareStore(useShallow((state) => state.getAllFilterReportGovernment));
  const getReportGovernment = reportFlareStore(useShallow((state) => state.getReportGovernment));
  const filterReportGovernment = reportFlareStore(useShallow((state) => state.filterReportGovernment));
  const dataReportGovernment = reportFlareStore(useShallow((state) => state.dataReportGovernment));

  // Excel Store Zone !
  const queryStr = excelStore(useShallow((state) => state.queryStr));
  const setQueryStringExcel = excelStore(useShallow((state) => state.setQueryStr));
  const setSelectedYearStore = excelStore(useShallow((state) => state.setSelectedYear));

  // console.log("[ExportReportGovernment] queryStr :: ", queryStr);

  const setQueryPdf = pdfStore(useShallow((state) => state.setFilter));

  const filterMonth = useMemo(() => {
    return MONTHS_LISTS.map((e) => ({
      label: capitalizeFirstLetter(e),
      value: e,
    }));
  }, []);

  const initialRequest = async () => {
    const responseFilter = await getAllFilterReportGovernment();
    const { monthInYear, flareName } = responseFilter;
    // console.log("monthInYear : ", monthInYear, flareName);
    const firstFlareName = flareName.length ? flareName[0].value : null;
    const firstMonthInYear = monthInYear.length ? monthInYear[0].value : null;
    setSelectedFlareName(firstFlareName);
    setSelectedYear(firstMonthInYear);
    const year = `${firstMonthInYear}`;
    const month = selectedMonth;
    // console.log("YEAR START : ", year);
    setQueryStringExcel(JSON.stringify({ year, month, flareName: firstFlareName }));
    setQueryPdf({ year: year ?? null, month: month ?? null, flareName: firstFlareName });
    getReportGovernment(year, month, firstFlareName);
  };

  useDidMount(() => {
    initialRequest();
  });

  useEffect(() => {
    initialRequest();
  }, [profile?.activeCompany]);

  const handleChangeYear = (val: string) => {
    setSelectedYear(val);
    const year = `${val}`;
    const month = selectedMonth;

    const tmpQuery = { ...JSON.parse(queryStr ? queryStr : "{}"), year, month };
    setSelectedYearStore(year);
    setQueryStringExcel(JSON.stringify(tmpQuery));

    setQueryPdf({ year, month, flareName: selectedFlareName });
    getReportGovernment(year, month, selectedFlareName);
  };

  const handleChangeMonth = (val: string) => {
    setSelectedMonth(val);
    const year = `${selectedYear}`;
    const month = val;

    const tmpQuery = { ...JSON.parse(queryStr ? queryStr : "{}"), year, month };
    setSelectedYearStore(year);
    setQueryStringExcel(JSON.stringify(tmpQuery));

    setQueryPdf({ year, month, flareName: selectedFlareName });
    getReportGovernment(year, month, selectedFlareName);
  };

  const handleChangeFlareName = (val: string) => {
    setSelectedFlareName(val);
    const year = `${selectedYear}`;
    const month = selectedMonth;

    const tmpQuery = { ...JSON.parse(queryStr ? queryStr : "{}"), flareName: val };
    setQueryStringExcel(JSON.stringify(tmpQuery));

    setQueryPdf({ year, month, flareName: val });
    getReportGovernment(year, month, val);
  };

  const handleButtonMenuClick = (buttonName: string) => {
    setSelectedButton(buttonName);
  };

  const buttonStyle = (buttonName: string) => ({
    backgroundColor: selectedButton === buttonName ? componentPalette.primary : "initial",
    color: selectedButton === buttonName ? "white" : "initial",
  });

  const renderRowsTitleCard = (level: any, title: string) => {
    return (
      <Row className="w-full rounded bg-white p-4 mb-2" style={{ height: 60 }}>
        <VerticalCenter>
          <div style={{ paddingLeft: level === 4 ? "6.5rem" : "5rem", paddingTop: "0.5em" }}>
            <Title level={level}>{title}</Title>
          </div>
        </VerticalCenter>
      </Row>
    );
  };
  const renderRowsContentCard = (title: string, content: string | number | undefined, unit: string, isCompound?: boolean) => {
    return (
      <Row className="w-full rounded bg-white p-4 mb-2" justify={"space-around"} style={{ height: 60 }}>
        <Col span={15} className={isCompound ? "pl-8" : "pl-4"}>
          <Text>{title}</Text>
        </Col>
        <Col span={6}>
          <Text>{content}</Text>
        </Col>
        <Col span={3}>
          <Text>{unit}</Text>
        </Col>
      </Row>
    );
  };

  return (
    <Row className="w-full">
      <Row>
        <Select
          value={selectedYear}
          style={{ minWidth: 100 }}
          onChange={handleChangeYear}
          options={filterReportGovernment.monthInYear}
          disabled={!canApproveAndViewMainReport(permissions)}
        />
        <Select
          value={selectedMonth}
          style={{ minWidth: 120, paddingLeft: "1rem" }}
          onChange={handleChangeMonth}
          options={filterMonth}
          disabled={!canApproveAndViewMainReport(permissions)}
        />
        <Select
          value={selectedFlareName}
          style={{ minWidth: 140, paddingLeft: "1rem" }}
          onChange={handleChangeFlareName}
          options={filterReportGovernment.flareName}
          disabled={!canApproveAndViewMainReport(permissions)}
        />
        <div style={{ paddingLeft: "1rem" }}>
          <Button
            size="middle"
            style={buttonStyle("list")}
            icon={<BsCardList style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("list")}
          />
          <Button
            size="middle"
            style={buttonStyle("table")}
            icon={<BsTable style={{ fontSize: "18px" }} />}
            onClick={() => handleButtonMenuClick("table")}
          />
        </div>
      </Row>

      {selectedButton === "list" && (
        <Row className="w-full" justify={"center"} style={canApproveAndViewMainReport(permissions) ? {} : { display: "none" }}>
          <Col span={19} className="mt-4">
            {
              <Spin className="w-full" spinning={loadingFilterReportGovernment || loading}>
                {renderRowsTitleCard(4, "ข้อมูลการใช้หอเผาทิ้ง")}
                {renderRowsTitleCard(5, "การใช้หอเผาทิ้งในภาวะปกติ")}
                {renderRowsContentCard(
                  "1. อัตราการไหลของมวลก๊าซที่ระบายไปสู่หอเผาทิ้งเฉลี่ย",
                  formatNumberWithRoundUpDecimal(
                    dataReportGovernment?.normal.flareVolumeNormal ? (dataReportGovernment?.normal.flareVolumeNormal as number) : 0,
                  ),
                  "ตัน/ชั่วโมง",
                )}
                {renderRowsContentCard("2. องค์ประกอบหลักของก๊าซที่ระบายไปสู่หอเผาทิ้ง", " ", " ")}
                {dataReportGovernment?.normal.compounds &&
                  dataReportGovernment.normal.compounds.map((item, index) => {
                    return (
                      <div key={"wrap-content-" + index}>
                        {renderRowsContentCard(
                          item.compoundName,
                          formatNumberWithRoundUpDecimal(item.sumValue as number, 2),
                          "สัดส่วนโดยมวล",
                          true,
                        )}
                      </div>
                    );
                  })}
                {renderRowsContentCard(
                  "3. ปริมาณรวมของสารอินทรีย์ระเหยง่ายที่ระบายจากหอเผาทิ้ง",
                  formatNumberWithRoundUpDecimal(dataReportGovernment?.normal.flareVocEmission as number, 2),
                  "กิโลกรัม",
                )}
                {renderRowsTitleCard(5, "การใช้หอเผาทิ้งในกรณี ซ่อมบำรุง หรือ เหตุฉุกเฉิน")}
                {renderRowsContentCard(
                  "1. อัตราการไหลของมวลก๊าซที่ระบายไปสู่หอเผาทิ้งเฉลี่ย",
                  formatNumberWithRoundUpDecimal(dataReportGovernment?.turnaroundWithEmergency.flareVolumeTurnEmergency as number),
                  "ตัน/ชั่วโมง",
                )}
                {renderRowsContentCard("2. องค์ประกอบหลักของก๊าซที่ระบายไปสู่หอเผาทิ้ง", " ", " ")}
                {dataReportGovernment?.turnaroundWithEmergency.compounds &&
                  dataReportGovernment.turnaroundWithEmergency.compounds.map((item, index) => {
                    return (
                      <div key={"wrap-content-" + index}>
                        {renderRowsContentCard(
                          item.compoundName,
                          formatNumberWithRoundUpDecimal(item.sumValue as number, 2),
                          "สัดส่วนโดยมวล",
                          true,
                        )}
                      </div>
                    );
                  })}
                {renderRowsContentCard(
                  "3. ปริมาณรวมของสารอินทรีย์ระเหยง่ายที่ระบายจากหอเผาทิ้ง",
                  formatNumberWithRoundUpDecimal(dataReportGovernment?.turnaroundWithEmergency.flareVocEmissionTurnEmergency as number, 2),
                  "กิโลกรัม",
                )}
              </Spin>
            }
          </Col>
        </Row>
      )}
    </Row>
  );
};

export default ReportGovernmentComponent;
